/**
 * One chart in the Library (W2-10 projects-data; DESIGN.md §4.1): thumbnail, title or level, note and line counts,
 * when it was edited, "changed since export" / "exported" and "recovered unsaved edits" indicators, and its menu
 * (Open, New difficulty, Duplicate, Export .pez, Version history, Rename, Download recovery data, Move to Trash,
 * plus entries other modules place in the 'library.card' menu).
 *
 * The card's main button opens the chart; it takes part in the list's roving focus (one tab stop for the list; the
 * focused card's menu button is the next stop) and is a `[data-keys="native"]` widget, so the list's keys reach
 * ProjectGrid before the app's global bindings. F2 / "Rename" turns the title into a text field: Enter keeps the
 * new title, Escape keeps the old one. State is never shown by colour alone: every indicator has an icon and words.
 */

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { ProjectSummary } from '../../persist/types';
import { Icon, IconButton, Menu, type MenuItem } from '../../ui';
import type { LibraryText } from './hooks';
import { useObjectUrl } from './hooks';
import { menuActionsFor, type CardAction } from './cardActions';
import { m } from './library.messages';
import { relativeTime } from './libraryModel';
import s from './Library.module.css';

export interface ProjectCardProps {
  readonly services: AppServices;
  readonly text: LibraryText;
  readonly project: ProjectSummary;
  readonly now: number;
  readonly view: 'grid' | 'list';
  /** Inside a song group: the level is the card's title and the song's title is shown only when it differs. */
  readonly groupTitle: string | null;
  readonly focused: boolean;
  readonly renaming: boolean;
  readonly busy: boolean;
  readonly onFocus: () => void;
  readonly onOpen: () => void;
  readonly onRename: (name: string | null) => void;
}

/** "edited 2 hours ago" / "edited just now". */
export function editedText(text: LibraryText, at: number, now: number): string {
  const when = relativeTime(at, now, text.locale);
  return when === null ? text.t(m.editedJustNow) : text.t(m.edited, { when });
}

/** Title of a project ("Untitled chart" when empty). */
export function titleOf(text: LibraryText, p: ProjectSummary): string {
  return p.name.trim() === '' ? text.t(m.untitled) : p.name;
}

/** Level display ("No level" when empty). */
export function levelOf(text: LibraryText, p: ProjectSummary): string {
  return p.level.trim() === '' ? text.t(m.noLevel) : p.level.trim();
}

/** "2,341 notes · 142 lines". */
export function statsText(text: LibraryText, p: ProjectSummary): string {
  return text.t(m.stats, { notes: p.noteCount, lines: p.lineCount });
}

export function Thumbnail(props: {
  readonly blob: Blob | null;
  readonly alt: string;
  readonly className?: string | undefined;
}) {
  const url = useObjectUrl(props.blob);
  return (
    <span className={`${s.thumb} ${props.className ?? ''}`}>
      {url === null ? <Icon name="Music" size={20} /> : <img src={url} alt={props.alt} className={s.thumbImg} />}
    </span>
  );
}

interface Indicator {
  readonly key: string;
  readonly icon: string;
  readonly label: string;
  readonly tip?: string;
  readonly tone: 'info' | 'muted' | 'warning' | 'ok';
}

/** "recovered unsaved edits", "changed since export" / "exported 3 days ago" / "not exported yet". */
export function indicatorsOf(text: LibraryText, p: ProjectSummary, now: number): Indicator[] {
  const items: Indicator[] = [];
  if (p.recoverable) {
    items.push({ key: 'rec', icon: 'History', label: text.t(m.recovered), tip: text.t(m.recoveredTip), tone: 'info' });
  }
  if (p.lastExportAt === null) {
    items.push({ key: 'never', icon: 'CircleDashed', label: text.t(m.neverExported), tone: 'muted' });
  } else if (p.changedSinceExport) {
    items.push({ key: 'changed', icon: 'CircleDot', label: text.t(m.changedSinceExport), tone: 'warning' });
  } else {
    const when = relativeTime(p.lastExportAt, now, text.locale) ?? text.t(m.justNow);
    items.push({ key: 'exported', icon: 'PackageCheck', label: text.t(m.exported, { when }), tone: 'ok' });
  }
  return items;
}

function Indicators(props: { readonly items: readonly Indicator[] }) {
  const { items } = props;
  return (
    <span className={s.flags}>
      {items.map((it) => (
        <span key={it.key} className={s.flag} data-tone={it.tone} title={it.tip}>
          <Icon name={it.icon} size={14} />
          <span>{it.label}</span>
        </span>
      ))}
    </span>
  );
}

function RenameField(props: {
  readonly text: LibraryText;
  readonly initial: string;
  readonly onDone: (name: string | null) => void;
}) {
  const { text, initial, onDone } = props;
  const [value, setValue] = useState(initial);
  const input = useRef<HTMLInputElement>(null);
  const finished = useRef(false);
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);
  const finish = (name: string | null): void => {
    if (finished.current) return;
    finished.current = true;
    onDone(name);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      finish(value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      finish(null);
    }
  };
  return (
    <label className={s.renameField}>
      <span className="visually-hidden">{text.t(m.renameLabel)}</span>
      <input
        ref={input}
        className={s.renameInput}
        value={value}
        maxLength={200}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          finish(value);
        }}
      />
    </label>
  );
}

export function ProjectCard(props: ProjectCardProps) {
  const { services, text, project: p, now, view, groupTitle, focused, renaming, busy } = props;
  const name = titleOf(text, p);
  const level = levelOf(text, p);
  const stats = statsText(text, p);
  const edited = editedText(text, p.updatedAt, now);
  const indicators = indicatorsOf(text, p, now);
  const inGroup = groupTitle !== null;
  const title = inGroup ? level : name;
  const subtitle = inGroup ? (p.name.trim() !== '' && p.name !== groupTitle ? name : null) : level;
  const showThumb = !inGroup || view === 'grid';

  const actions: readonly CardAction[] = menuActionsFor(services, p);
  const items: MenuItem[] = actions.map((a): MenuItem =>
    a.kind === 'separator'
      ? { kind: 'separator' }
      : a.kind === 'command'
        ? { kind: 'command', command: a.command, args: { id: p.id } }
        : {
            kind: 'action',
            label: text.t(a.label),
            icon: a.icon,
            disabledReason: busy ? { key: m.busy } : null,
            onSelect: () => {
              void a.run();
            },
          },
  );

  return (
    <li className={s.card} data-view={view} data-project-id={p.id} data-busy={busy || undefined}>
      {renaming ? (
        <div className={s.cardMain} data-renaming>
          {showThumb && <Thumbnail blob={p.thumbnail} alt={text.t(m.thumbnailAlt, { name })} />}
          <span className={s.cardText}>
            <RenameField text={text} initial={p.name} onDone={props.onRename} />
            <span className={s.cardStats}>{stats}</span>
          </span>
        </div>
      ) : (
        <button
          type="button"
          className={s.cardMain}
          data-card-main
          data-project-id={p.id}
          // The list's own keys (arrows, Home / End, Enter, Space, F2, Delete, typing) come first (ProjectGrid).
          data-keys="native"
          tabIndex={focused ? 0 : -1}
          aria-label={text.t(m.cardLabel, {
            name,
            level,
            stats,
            edited,
            flags: indicators.map((i) => i.label).join(', '),
          })}
          aria-busy={busy || undefined}
          onFocus={props.onFocus}
          onClick={props.onOpen}
        >
          {showThumb && <Thumbnail blob={p.thumbnail} alt="" />}
          <span className={s.cardText}>
            <span className={s.cardTitleRow}>
              <span className={s.cardTitle}>{title}</span>
              {subtitle !== null && <span className={s.cardSubtitle}>{subtitle}</span>}
            </span>
            <span className={s.cardStats}>{stats}</span>
            <span className={s.cardEdited}>{edited}</span>
            <Indicators items={indicators} />
          </span>
        </button>
      )}
      <span className={s.cardMenu}>
        <Menu
          label={text.t(m.menuLabel, { name })}
          align="end"
          trigger={
            <IconButton
              icon="Ellipsis"
              size="sm"
              label={{ key: m.menuLabel, params: { name } }}
              tabIndex={focused ? 0 : -1}
            />
          }
          items={items}
        />
      </span>
    </li>
  );
}
